/** Console adoption of the governed page composition (#2033). */
import { useMemo } from 'react'
import {
  dashboardDrillForRow, parseDescribedPage, type DescribedPageBlock, type DescribedItem,
} from '@solum/experience'
import { DashboardWidget, PageComposition, type ExperienceState } from '@solum/ui'
import { useConsolePage, useMeasures, useDashboardItemContents } from '../../data/queries'
import { Home } from './Home'
import { readableError } from '@solum/sdk'

/**
 * THE CONSOLE'S BINDING OF ONE DESCRIBED PAGE — the data, the widget states and
 * the block adapter — shared by every Console destination that consumes a
 * governed page (#2940), so a second destination reuses this one rather than
 * copying a renderer. The host keeps its own page query (GovernedHome reads
 * `console.home`, Onboarding reads `console.onboarding`) and its own state
 * branches; only `PageComposition` lays the page out.
 *
 * `onOpenRecord`, when a host passes it, opens a list or chart row whose item
 * declares a RECORD drill — through the host's navigation, which re-checks
 * admission. A FILTER drill names a page key this host has no route for, so it
 * is not offered: an unopenable row is honest, a guessed route is not. Home
 * passes nothing and is unchanged.
 */
export function useGovernedPageBinding(
  query: ReturnType<typeof useConsolePage>,
  opts: { onOpenRecord?: (entity: string, id: string) => void } = {},
) {
  const page = useMemo(() => parseDescribedPage(query.data), [query.data])
  const items = useMemo(
    () => page.blocks.map(block => block.descriptor).filter((item): item is DescribedItem => item !== null),
    [page.blocks],
  )
  const keys = useMemo(
    () => items.map(item => item.source.measureKey).filter((key): key is string => !!key),
    [items],
  )
  const measures = useMeasures(keys)
  const contents = useDashboardItemContents(items)
  const byKey = useMemo(() => new Map((measures.data ?? []).map(row => [row.key, row])), [measures.data])
  const measureReadFailure = measures.isError
    ? readableError(measures.error, 'The figures could not be read.')
    : null

  const renderBlock = (block: DescribedPageBlock) => {
    if ((block.kind ?? 'widget') !== 'widget' || block.descriptor === null) {
      return (
        <div role="status" data-page-block-adapter={block.kind ?? 'widget'}>
          This governed {block.kind ?? 'page'} block is not available in the Console host yet.
        </div>
      )
    }
    const item = block.descriptor!
    /* Identity is the parsed descriptor object carried by THIS block, not
       `itemKey` — that is unique only inside one dashboard, and a page may
       compose two dashboards that both call an item `summary`. */
    const index = items.indexOf(item)
    const read = index < 0 ? undefined : contents[index]
    const measure = item.source.measureKey ? byKey.get(item.source.measureKey) : undefined
    const failure = read?.error instanceof Error ? read.error.message
      : read?.error ? String(read.error) : null
    const state: ExperienceState = measureReadFailure
      ? { kind: 'error', title: 'This figure could not be read', message: measureReadFailure }
      : measure?.error
      ? { kind: 'error', title: 'This figure could not be calculated', message: measure.error }
      : failure
      ? { kind: 'error', title: 'This could not be read', message: failure }
      : (item.kind === 'list' || item.kind === 'chart') && read?.isLoading
      ? { kind: 'loading', label: 'Reading your data…' }
      : measures.isLoading && !!item.source.measureKey
        ? { kind: 'loading', label: 'Calculating this figure…' }
        : { kind: 'ready' }
    const descriptor = item.drillDescriptor
    const openRecord = opts.onOpenRecord
    const drill = openRecord && descriptor?.context.kind === 'record'
      ? (row: Record<string, unknown>) => dashboardDrillForRow(descriptor, row)
      : null
    const onOpen = drill && openRecord
      ? (row: Record<string, unknown>) => {
          const destination = drill(row)
          if (destination?.recordType && destination.recordId) openRecord(destination.recordType, destination.recordId)
        }
      : undefined
    const canOpen = drill ? (row: Record<string, unknown>) => drill(row) !== null : undefined
    const data = read?.data ?? null
    return <DashboardWidget
      item={item} state={state} value={measure?.value ?? undefined}
      list={item.kind === 'list' && data && data.rows.length > 0
        ? { columns: data.columns, rows: data.rows, onOpen, canOpen } : undefined}
      chart={item.kind === 'chart' && data?.chart
        ? { definition: data.chart, rows: data.rows, onOpen, canOpen } : undefined}
      testId={`console-page-widget-${block.id}`}
    />
  }

  return { page, renderBlock }
}

export function GovernedHome() {
  const query = useConsolePage('console.home')
  const { page, renderBlock } = useGovernedPageBinding(query)

  if (query.isPending) {
    return <PageComposition page={page} title="Home"
      state={{ kind: 'loading', label: 'Loading your workspace…' }} renderBlock={() => null}
      testId="console-governed-home-loading" />
  }
  if (query.isError) {
    return <PageComposition page={{ ...page, state: 'error', reason: 'The governed page could not be read.' }}
      title="Home" state={{ kind: 'error', title: 'Your workspace could not be loaded',
        message: 'The page description is unavailable.', onRetry: () => { void query.refetch() } }}
      renderBlock={() => null} testId="console-governed-home-error" />
  }
  /* Only a GENUINE empty may fall through to the native screen (ROOT 68189
     item 1). A malformed or refused composition is a governed refusal: the
     deployment authored something and we could not read it, and quietly
     rendering the built-in home hides that from the only person who could fix
     it. `state` is the server's word — see `parseDescribedPage`. */
  if (!page.resolved && page.state === 'empty') return <Home />
  if (!page.resolved) {
    return <PageComposition
      page={{ ...page, state: 'error', reason: page.reason ?? 'The governed page could not be read.' }}
      title="Home"
      state={{ kind: 'error', title: 'Your workspace could not be shown',
        message: page.reason ?? 'The page composition could not be read safely.',
        onRetry: () => { void query.refetch() } }}
      renderBlock={() => null} testId="console-governed-home-refused" />
  }

  return <PageComposition page={page} title="Home" eyebrow="Workspace"
    summary="Your governed workspace composition." renderBlock={renderBlock}
    testId="console-governed-home" />
}
